<template>
  <v-container ref="AdminPanel">
    <v-row class="AdminPanel-section">
      <v-toolbar-title>{{ $t('adminPanel.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn text small @click="logout">Logout</v-btn>
    </v-row>
    <v-row class="AdminPanel-section">
      <v-col cols="3">
        <v-text-field
          v-model="searchText"
          prepend-inner-icon="mdi-magnify"
          :label="$t('adminPanel.searchLabel')"
          clearable
        />
      </v-col>
      <v-col cols="1" class="pl-7">
        <v-btn icon color="green" x-large :disabled="!adminShops.hash" @click="download">
          <span>Export</span><v-icon>mdi-google-spreadsheet</v-icon>
        </v-btn>
      </v-col>
      <v-col cols="1" class="pl-10">
          <input id="fileUpload" ref="file" type="file" accept=".csv" @change="importCsv" hidden>
        <v-btn icon color="green" x-large @click="importFileChoose()">
          <span>Import</span><v-icon>mdi-upload-outline</v-icon>
        </v-btn>
      </v-col>
      <v-col cols="2" offset="5">
        <v-select
          v-model="selectedCountry"
          background-color="secondary lighten-1"
          class="ma-2 Calendar-select"
          dense
          flat
          height="55px"
          hide-details
          :items="countries"
          light
          :placeholder="$t('adminPanel.selectCountryPlaceholder')"
          solo
        />
      </v-col>
    </v-row>
      <v-row class="AdminPanel-section" v-if="success || error">
          <v-col>
          <v-btn text small @click="success = error = null">Clear messages</v-btn>
              <v-alert v-if="success" color="success" data-e2e="accountSuccessMessage" type="error" text dense>
                  <li class="messageListItem" v-for="item in success" :key="item">
                      {{ item }}
                  </li>
              </v-alert>
              <v-alert v-if="error" color="error" data-e2e="accountErrorMessage" type="error" text dense>
                  <li class="messageListItem" v-for="item in error" :key="item">
                      {{ item }}
                  </li>
              </v-alert>
          </v-col>
      </v-row>

    <v-row class="AdminPanel-section">
      <v-col cols="12">
        <v-data-table
          item-key="shopUuid"
          :loading="loading"
          :loading-text="$t('adminPanel.loadingText')"
          :no-data-text="$t('adminPanel.noDataText')"
          :no-results-text="$t('adminPanel.noResultsText')"
          :headers="headers"
          :items="adminShops ? adminShops.shops : []"
          :server-items-length="total"
          :footer-props="{
            'items-per-page-options': [10, 20, 50],
            'items-per-page-text': $t('adminPanel.itemsPerPageText'),
            pageText: `${this.options.page} of ${Math.ceil(this.total / this.options.itemsPerPage)}`
          }"
          :options.sync="options"
        >
          <template v-slot:[`item.owner`]="{ item }">
            <span>{{ item.owner.email || '-  -' }}</span>
          </template>
          <template v-slot:[`item.location`]="{ item }">
            <a v-if="item.latitude" :href="getMapLink(item)" target="_blank"
              >map<v-icon primary--text right x-small>mdi-open-in-new</v-icon>
            </a>
          </template>
          <template v-slot:[`item.isOnboarded`]="{ item }">
            <span>{{ getStatus(item) }}</span>
          </template>
          <template v-slot:[`item.shopUuid`]="{ item }">
            <v-menu>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-cog</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item v-for="(action, index) in getActions(item)" :key="index" link>
                  <v-list-item-title @click="action.method(item)">{{ action.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-snackbar v-model="snackbar" text-center :timeout="2000"> Link copied to clipboard </v-snackbar>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'
import auth from '@/services/auth'
// eslint-disable-next-line no-unused-vars
import axios from 'axios'

const GET_SHOPS = gql`
  query adminShops(
    $searchText: String
    $sortOptions: SortShopOptionInput
    $country: [String]
    $limit: Int
    $offset: Int
  ) {
    adminShops(searchText: $searchText, sortOptions: $sortOptions, country: $country, limit: $limit, offset: $offset) {
      hash
      total
      shops {
        ownerUuid
        owner {
          ecpId
          email
          resetPasswordLink
          isEmailInSuppressionList
          hasPassword
        }
        enabled
        ezContentId
        country
        displayName
        city
        address1
        address2
        latitude
        longitude
        isOnboarded
        shopUuid
        invitationLink
      }
    }
  }
`

const DELETE_EMAIL_FROM_SUPPRESSION_LIST = gql`
  mutation deleteEmailFromSuppressionList($email: String!) {
    deleteEmailFromSuppressionList(email: $email)
  }
`

export default {
  name: 'AdminPanel',

  data() {
    return {
      adminShops: {
        hash: undefined,
        total: 0,
        shops: []
      },
      loading: true,
      total: 0,
      serverItemsLength: 0,
      searchText: '',
      selectedCountry: '',
      snackbar: false,
      sort: {
        field: null,
        desc: false
      },
      options: {},
      success: false,
      error: false
    }
  },

  computed: {
    headers() {
      return [
        { sortable: true, text: this.$t('adminPanel.headers.ownerUuid'), value: 'owner.ecpId' },
        { sortable: true, text: this.$t('adminPanel.headers.owner'), value: 'owner.email' },
        { sortable: false, text: this.$t('adminPanel.headers.country'), value: 'country' },
        { sortable: true, text: this.$t('adminPanel.headers.displayName'), value: 'displayName' },
        { sortable: true, text: this.$t('adminPanel.headers.city'), value: 'city' },
        { sortable: true, text: this.$t('adminPanel.headers.address1'), value: 'address1' },
        { sortable: false, text: this.$t('adminPanel.headers.address2'), value: 'location' },
        { sortable: false, text: this.$t('adminPanel.headers.isOnboarded'), value: 'isOnboarded' },
        { sortable: false, text: this.$t('adminPanel.headers.shopUuid'), value: 'shopUuid' }
      ]
    },
    countries() {
      var countries = Object.keys(this.$const.siteData[this.$site].countries).map(key => ({
        text: this.$const.siteData[this.$site].countries[key].name,
        value: key
      }))

      countries = countries.filter(country => {
        if (this.$store.state.myUser.role === this.$const.userRoles.COUNTRY_MANAGER) {
          if (this.$store.state.myUser.admin.countries.includes(country.value)) {
            return country
          }
        } else {
          return country
        }
      })

      countries.unshift({ text: 'All countries', value: '' })

      return countries
    }
  },

  apollo: {
    adminShops: {
      debounce: 300,
      query: GET_SHOPS,
      variables() {
        // eslint-disable-next-line no-unused-vars
        const countries = this.$store.state.myUser.admin.countries

        const query = {}
        if (this.$store.state.myUser.role === this.$const.userRoles.COUNTRY_MANAGER && this.selectedCountry === '') {
          this.selectedCountry = countries
        }
        if (this.selectedCountry) {
          query.country = this.selectedCountry
        }
        if (this.searchText) {
          query.searchText = this.searchText
        }
        if (this.sort.field) {
          query.sortOptions = {
            field: this.sort.field,
            isDesc: this.sort.desc
          }
        }
        if (this.options.page) {
          query.offset = (this.options.page - 1) * this.options.itemsPerPage
          query.limit = this.options.itemsPerPage
        }
        return query
      },
      result({ data }) {
        this.loading = false
        this.total = this.$get(data, 'adminShops.total', 0)
      }
    }
  },

  created() {
    if (
      !this.$store.state.myUser ||
      (this.$store.state.myUser.role !== this.$const.userRoles.COUNTRY_MANAGER &&
        this.$store.state.myUser.role !== this.$const.userRoles.ADMIN)
    ) {
      this.$router.push({ name: 'Calendar' })
    }
  },

  watch: {
    options() {
      this.loading = true

      if (this.options.sortBy.length && this.options.sortDesc.length) {
        this.sort.field = this.options.sortBy[0]
        this.sort.desc = this.options.sortDesc[0]
      } else {
        this.sort.field = null
        this.sort.desc = false
      }
    },
    searchText() {
      this.loading = true
      if (this.options.page) {
        this.options.page = 1
      }
    }
  },

  methods: {
    copyPasswordLink(shop) {
      this.copyLink(shop.owner ? shop.owner.resetPasswordLink : '')
    },
    copyInvitationLink(shop) {
      this.copyLink(shop.invitationLink)
    },
    download() {
      window.open(`/static/once/adminCsv/${this.adminShops.hash}`)
    },
    importCsv(e) {
        const file = e.target.files[0]
        const formData = new FormData()
        // TODO: Refactor wrong approach
        formData.append('operations', JSON.stringify({
          'user': this.$store.state.myUser
        }))
        formData.append('map', JSON.stringify({
            'file': ['variables.file']
        }))
        formData.append('file', file)

        axios.post(`/adminCsv/import`, formData)
        .then(response => {
          if (response.data.errors.length !== 0) {
            this.error = response.data.errors
          }
          if (response.data.messages.length !== 0) {
            this.success = response.data.messages
          }
        })
        .catch()
    },
    importFileChoose() {
        document.getElementById("fileUpload").click()
    },
    getActions(shop) {
      const menuItems = []

      if (this.getStatus(shop) === 'IMPORTED') {
        menuItems.push({
          method: this.copyInvitationLink,
          label: this.$t('adminPanel.copyInvitationLink')
        })
      } else {
        menuItems.push({
          method: this.copyPasswordLink,
          label: this.$t('adminPanel.copyPasswordLink')
        })
      }

      if (shop.owner && shop.owner.isEmailInSuppressionList) {
        menuItems.push({
          method: this.removeEmail,
          label: this.$t('adminPanel.removeEmail')
        })
      }

      if (this.getStatus(shop) === 'ONBOARDED') {
        menuItems.push({
          method: this.impersonateUser,
          label: 'Edit'
        })
      }

      return menuItems
    },
    getMapLink(shop) {
      return `https://www.google.com/maps/search/?api=1&query=${shop.latitude},${shop.longitude}`
    },
    getStatus(shop) {
      if (shop.owner && !shop.owner.hasPassword) {
        return 'IMPORTED'
      }

      if (shop.owner && shop.owner.hasPassword && !shop.isOnboarded) {
        return 'REGISTERED'
      }

      if (shop.owner && shop.owner.hasPassword && shop.isOnboarded && shop.enabled) {
        return 'ONBOARDED'
      }

      if (shop.owner && shop.owner.hasPassword && shop.isOnboarded && !shop.enabled) {
        return 'DISABLED'
      }

      return 'no match'
    },
    removeEmail(shop) {
      if (!shop.owner || !shop.owner.email) {
        return
      }
      this.$apollo.mutate({
        mutation: DELETE_EMAIL_FROM_SUPPRESSION_LIST,
        variables: {
          email: shop.owner.email
        }
      })
    },
    copyLink(currentLink) {
      navigator.clipboard.writeText(currentLink)
      this.snackbar = true
    },
    async logout() {
      await auth.logout()
      await this.$router.push({ name: 'Login' })
      this.$store.commit('RESET_STORE')
    },
    async impersonateUser(shop) {
      const request = await fetch(`/auth/get-jwt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ecpId: shop.owner.ecpId
        })
      })
      const response = await request.json()
      if (response.token) {
        await auth.logout()
        await auth.loginWithToken(response.token)
        await this.$router.push({name: 'Home'})
        this.$store.commit('RESET_STORE')
        window.location.reload()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.AdminPanel {
  background-color: var(--v-secondary-lighten1);
  color: var(--v-secondary-base);
}

.AdminPanel-section {
  background: white;
  margin-bottom: 20px;
  padding: 20px;
}

.messageListItem {
  list-style: none;
}
</style>
